import { useCallback, useState } from "react";
import {
  Title,
  Text,
  Paper,
  Stack,
  Group,
  Button,
  Avatar,
  Divider,
  Loader,
  Menu,
  ActionIcon,
  Modal,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { IconDots, IconEdit, IconTrash } from "@tabler/icons-react";
import axios from "axios";
import dayjs from "dayjs";
import { useAuthStore } from "../../store/auth.store";
import { sanitizeHtml } from "../../utils/sanitize";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
// Edits within this window of creation don't show the "edited" indicator;
// avoids confusion when the UpdateDateColumn fires on the initial save.
const EDIT_INDICATOR_THRESHOLD_SECONDS = 5;

interface ObservationComment {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface ObservationCommentsThreadProps {
  observationId: string;
  displayCode?: string | null;
}

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authorInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function wasEdited(comment: ObservationComment): boolean {
  return (
    dayjs(comment.updatedAt).diff(comment.createdAt, "second") >
    EDIT_INDICATOR_THRESHOLD_SECONDS
  );
}

export default function ObservationCommentsThread({
  observationId,
  displayCode,
}: ObservationCommentsThreadProps) {
  const queryClient = useQueryClient();
  const queryKey = ["observation-comments", observationId];
  const profile = useAuthStore((state) => state.profile);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentToDelete, setCommentToDelete] =
    useState<ObservationComment | null>(null);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4] },
        codeBlock: false,
      }),
      Placeholder.configure({ placeholder: "Add a comment…" }),
      Underline,
      Link,
    ],
    content: "",
    onUpdate: ({ editor: ed }) => {
      setIsEditorEmpty(ed.isEmpty);
    },
  });

  const editEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4] },
        codeBlock: false,
      }),
      Placeholder.configure({ placeholder: "Edit comment…" }),
      Underline,
      Link,
    ],
    content: "",
  });

  const { data, isLoading, error } = useQuery<ObservationComment[]>({
    queryKey,
    queryFn: async () => {
      const res = await axios.get<{
        success: boolean;
        data: ObservationComment[];
      }>(`${API_BASE_URL}/api/observations/${observationId}/comments`, {
        headers: getAuthHeaders(),
      });
      return res.data.data;
    },
    enabled: !!observationId,
  });

  const addComment = useMutation({
    mutationFn: async (body: string) => {
      await axios.post(
        `${API_BASE_URL}/api/observations/${observationId}/comments`,
        { body },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
    },
    onSuccess: () => {
      editor?.commands.clearContent();
      setIsEditorEmpty(true);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      notifications.show({
        title: "Could not add comment",
        message: err instanceof Error ? err.message : "Unknown error",
        color: "red",
      });
    },
  });

  const editComment = useMutation({
    mutationFn: async ({
      commentId,
      body,
    }: {
      commentId: string;
      body: string;
    }) => {
      await axios.patch(
        `${API_BASE_URL}/api/observations/${observationId}/comments/${commentId}`,
        { body },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
    },
    onSuccess: () => {
      setEditingCommentId(null);
      editEditor?.commands.clearContent();
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      const status =
        axios.isAxiosError(err) && err.response?.status
          ? err.response.status
          : null;
      const message =
        status === 403
          ? "You can only edit your own comments."
          : err instanceof Error
          ? err.message
          : "Unknown error";
      notifications.show({
        title: "Could not save edit",
        message,
        color: "red",
      });
    },
  });

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      await axios.delete(
        `${API_BASE_URL}/api/observations/${observationId}/comments/${commentId}`,
        { headers: getAuthHeaders() }
      );
    },
    onSuccess: () => {
      closeDeleteModal();
      setCommentToDelete(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      const status =
        axios.isAxiosError(err) && err.response?.status
          ? err.response.status
          : null;
      const message =
        status === 403
          ? "You can only delete your own comments."
          : err instanceof Error
          ? err.message
          : "Unknown error";
      notifications.show({
        title: "Could not delete comment",
        message,
        color: "red",
      });
    },
  });

  const handleSubmit = useCallback(() => {
    if (!editor || editor.isEmpty || addComment.isPending) return;
    const html = editor.getHTML();
    addComment.mutate(html);
  }, [editor, addComment]);

  const handleStartEdit = useCallback(
    (comment: ObservationComment) => {
      if (!editEditor) return;
      setEditingCommentId(comment.id);
      editEditor.commands.setContent(comment.body);
    },
    [editEditor]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingCommentId(null);
    editEditor?.commands.clearContent();
  }, [editEditor]);

  const handleSaveEdit = useCallback(
    (commentId: string) => {
      if (!editEditor || editEditor.isEmpty || editComment.isPending) return;
      const html = editEditor.getHTML();
      editComment.mutate({ commentId, body: html });
    },
    [editEditor, editComment]
  );

  const handleRequestDelete = useCallback(
    (comment: ObservationComment) => {
      setCommentToDelete(comment);
      openDeleteModal();
    },
    [openDeleteModal]
  );

  const handleCancelDelete = useCallback(() => {
    closeDeleteModal();
    setCommentToDelete(null);
  }, [closeDeleteModal]);

  const handleConfirmDelete = useCallback(() => {
    if (!commentToDelete) return;
    deleteComment.mutate(commentToDelete.id);
  }, [commentToDelete, deleteComment]);

  return (
    <Paper withBorder p="md" style={{ maxWidth: 650, width: "100%" }}>
      <Title order={4} mb="sm">
        {displayCode ? `Comments on ${displayCode}` : "Comments"}
      </Title>

      {isLoading && <Loader size="sm" />}

      {error && (
        <Text c="red" size="sm">
          Failed to load comments:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </Text>
      )}

      {!isLoading && !error && (
        <Stack gap="md">
          {(data ?? []).length === 0 ? (
            <Text c="dimmed" size="sm">
              No comments yet. Start the conversation below.
            </Text>
          ) : (
            (data ?? []).map((comment) => {
              const isMine = profile?.id === comment.authorId;
              const isEditing = editingCommentId === comment.id;
              const edited = wasEdited(comment);
              return (
                <Group
                  key={comment.id}
                  align="flex-start"
                  gap="sm"
                  wrap="nowrap"
                >
                  <Avatar radius="xl" size="md" color="blue">
                    {authorInitials(comment.authorName) || "?"}
                  </Avatar>
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Group gap="xs" justify="space-between" wrap="nowrap">
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={600}>
                          {comment.authorName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dayjs(comment.createdAt).format(
                            "MMM D, YYYY h:mm A"
                          )}
                        </Text>
                        {edited && (
                          <Text size="xs" c="dimmed" fs="italic">
                            (edited{" "}
                            {dayjs(comment.updatedAt).format(
                              "MMM D, YYYY h:mm A"
                            )}
                            )
                          </Text>
                        )}
                      </Group>
                      {isMine && !isEditing && (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              aria-label="Comment actions"
                            >
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => handleStartEdit(comment)}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconTrash size={14} />}
                              color="red"
                              onClick={() => handleRequestDelete(comment)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Group>
                    {isEditing ? (
                      <Stack gap="xs">
                        <RichTextEditor editor={editEditor}>
                          <RichTextEditor.Toolbar sticky stickyOffset={0}>
                            <RichTextEditor.ControlsGroup>
                              <RichTextEditor.Bold />
                              <RichTextEditor.Italic />
                              <RichTextEditor.Underline />
                              <RichTextEditor.Strikethrough />
                            </RichTextEditor.ControlsGroup>
                            <RichTextEditor.ControlsGroup>
                              <RichTextEditor.BulletList />
                              <RichTextEditor.OrderedList />
                            </RichTextEditor.ControlsGroup>
                            <RichTextEditor.ControlsGroup>
                              <RichTextEditor.Link />
                              <RichTextEditor.Unlink />
                            </RichTextEditor.ControlsGroup>
                          </RichTextEditor.Toolbar>
                          <RichTextEditor.Content />
                        </RichTextEditor>
                        <Group justify="flex-end" gap="xs">
                          <Button
                            size="xs"
                            variant="subtle"
                            onClick={handleCancelEdit}
                            disabled={editComment.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="xs"
                            onClick={() => handleSaveEdit(comment.id)}
                            loading={editComment.isPending}
                          >
                            Save
                          </Button>
                        </Group>
                      </Stack>
                    ) : (
                      <div
                        className="observation-comment-body"
                        style={{ wordBreak: "break-word" }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(comment.body),
                        }}
                      />
                    )}
                  </Stack>
                </Group>
              );
            })
          )}

          <Divider />

          <RichTextEditor editor={editor}>
            <RichTextEditor.Toolbar sticky stickyOffset={0}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
                <RichTextEditor.Strikethrough />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link />
                <RichTextEditor.Unlink />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
            <RichTextEditor.Content />
          </RichTextEditor>

          <Group justify="flex-end">
            <Button
              onClick={handleSubmit}
              loading={addComment.isPending}
              disabled={isEditorEmpty}
            >
              Add comment
            </Button>
          </Group>
        </Stack>
      )}

      <Modal.Root
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        size="sm"
        centered
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Delete comment</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Text size="sm">Delete this comment? This cannot be undone.</Text>
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button
              variant="default"
              onClick={handleCancelDelete}
              disabled={deleteComment.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirmDelete}
              loading={deleteComment.isPending}
            >
              Delete
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </Paper>
  );
}
